/* ============================================================================
 * 基础组件：Badge / Chip / Button / Panel / Kpi / EmptyState / Skeleton
 *
 * F-66  V2 把 83 种文案塞进同一个 badge/chip 视觉组件：状态、等级、分类、
 *       筛选器、口径、方法混在一起。
 * F-67  更要命的是筛选器 chip 与只读状态 badge 外观不可区分，用户看不出哪个能点。
 *       V3 强制分家：
 *         Badge = 只读状态，方角(--r-xs)、无边框、无 hover、非可聚焦
 *         Chip  = 可交互筛选，药丸(--r-pill)、有边框、有 hover/选中态、可聚焦
 * F-47  空态在 V2 全站只有 1 种 → EmptyState 提供五级语义
 * F-45  V2 骨架屏 0 个 → Skeleton
 * ========================================================================== */
import type { ReactNode, ButtonHTMLAttributes } from 'react';
import { Icon, type IconName } from './Icon';
import { ROLES, CAN_NAME } from '@/domain/master';
import { useSession, useRole } from '@/store/session';

/* ── Badge：只读状态标记 ─────────────────────────────────────────────── */

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand';

export function Badge({ tone = 'neutral', dot, children }: {
  tone?: BadgeTone; dot?: boolean; children: ReactNode;
}) {
  return (
    <span className={`badge badge--${tone}`}>
      {dot && <i className="badge-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/* ── Chip：可交互筛选器 ──────────────────────────────────────────────── */

export function Chip({ selected, onClick, count, children }: {
  selected?: boolean; onClick?: () => void; count?: number; children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`chip${selected ? ' chip--on' : ''}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      {children}
      {count !== undefined && <span className="chip-count">{count}</span>}
    </button>
  );
}

/* ── Button ─────────────────────────────────────────────────────────── */

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type BtnSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  icon?: IconName;
  /** 加载态：自动禁用并把文案换成 loadingText（F-44：V2 求解按钮点了毫无反馈）*/
  loading?: boolean;
  loadingText?: string;
  /**
   * 需要的权限键（如 `runSolver` / `editOrder`）。当前角色没有时按钮自动禁用，
   * 并把「谁没有什么权限」写进 title，鼠标悬停即可看见。
   *
   * ⚠ 这是**可见性**的底层注入点，不是准入控制——真正的准入在服务端
   * （`api/app/auth.py::requires`）。放在这里是为了不让用户点了才发现没权限：
   * 实测管理员在产能衔接页点「运行对账」，要等到服务端返回 403 才知道自己不能跑。
   */
  requires?: string;
}

export function Button({
  variant = 'secondary', size = 'md', icon, loading, loadingText, requires,
  children, disabled, className, title, ...rest
}: ButtonProps) {
  const can = useSession((s) => s.can);
  const role = useRole();
  const denied = !!requires && !can(requires);
  const reason = denied
    ? `${ROLES[role].name}没有「${CAN_NAME[requires] ?? requires}」权限，该动作由服务端校验`
    : undefined;
  return (
    <button
      type="button"
      className={`btn btn--${variant} btn--${size}${loading ? ' is-loading' : ''}${className ? ' ' + className : ''}`}
      disabled={disabled || loading || denied}
      aria-busy={loading || undefined}
      title={reason ?? title}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      <span>{loading ? (loadingText ?? '处理中…') : children}</span>
    </button>
  );
}

/* ── Panel ──────────────────────────────────────────────────────────── */

export function Panel({ title, sub, actions, flush, headingLevel = 2, className, children }: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  /** F-60：V2 面板标题是 div，全站无 H2/H3。此处默认渲染真 h2。 */
  headingLevel?: 2 | 3;
  className?: string;
  children: ReactNode;
}) {
  const H = (headingLevel === 3 ? 'h3' : 'h2') as 'h2' | 'h3';
  return (
    <section className={`panel${className ? ' ' + className : ''}`}>
      <header className="hd">
        <div className="ellip">
          <H>{title}</H>
          {sub && <div className="sub ellip">{sub}</div>}
        </div>
        {actions && <div className="hd-actions">{actions}</div>}
      </header>
      <div className={`bd${flush ? ' flush' : ''}`}>{children}</div>
    </section>
  );
}

/* ── KPI ────────────────────────────────────────────────────────────── */

export function Kpi({ label, value, unit, delta, deltaDir, sub, onTrace, glossary }: {
  label: ReactNode;
  /** 已经过 format.ts 格式化的字符串，组件不再二次加工 */
  value: string;
  unit?: string;
  delta?: string;
  deltaDir?: 'up' | 'down';
  sub?: ReactNode;
  onTrace?: () => void;
  glossary?: string;
}) {
  const body = (
    <>
      <div className="lab">
        {label}
        {glossary && <Icon name="info" size={12} label={`名词解读：${typeof label === 'string' ? label : ''}`} />}
      </div>
      <div className="val">
        {value}
        {unit && <span className="u">{unit}</span>}
      </div>
      <div className="sub">
        {delta && <span className={`delta${deltaDir ? ' ' + deltaDir : ''}`}>{delta}</span>}
        {sub}
      </div>
    </>
  );
  if (!onTrace) return <div className="kpi">{body}</div>;
  return (
    <button type="button" className="kpi kpi--traceable" onClick={onTrace} title="点击查看计算溯源">
      {body}
    </button>
  );
}

/* ── 空态五级（F-47）─────────────────────────────────────────────────── */

export type EmptyKind = 'no-data' | 'no-result' | 'error' | 'forbidden' | 'first-run';

const EMPTY_PRESET: Record<EmptyKind, { icon: IconName; title: string; hint: string }> = {
  'no-data': { icon: 'box', title: '暂无数据', hint: '该口径下当前没有记录' },
  'no-result': { icon: 'search', title: '没有匹配结果', hint: '试试放宽筛选条件，或清空后重新检索' },
  error: { icon: 'warn', title: '加载失败', hint: '数据未能取回，可重试或联系数据智能中心' },
  // A4 之后角色来自登录令牌，顶栏不再能切角色——提示语跟着改，否则是在教用户做做不到的事
  forbidden: { icon: 'shield', title: '无权访问', hint: '当前角色不具备该模块权限，请联系管理员调整角色，或换用有权限的账号登录' },
  'first-run': { icon: 'sparkle', title: '还没有开始', hint: '完成一次配置后，这里会显示结果' },
};

export function EmptyState({ kind = 'no-data', title, hint, action }: {
  kind?: EmptyKind; title?: string; hint?: ReactNode; action?: ReactNode;
}) {
  const p = EMPTY_PRESET[kind];
  return (
    <div className="empty" role={kind === 'error' ? 'alert' : undefined}>
      <Icon name={p.icon} size={28} />
      <div className="empty-title">{title ?? p.title}</div>
      <div className="empty-hint">{hint ?? p.hint}</div>
      {action}
    </div>
  );
}

/* ── 骨架屏（F-45）───────────────────────────────────────────────────── */

export function Skeleton({ rows = 3, height = 14 }: { rows?: number; height?: number }) {
  return (
    <div className="skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-bar" style={{ height, width: `${100 - (i % 3) * 12}%` }} />
      ))}
    </div>
  );
}
